import MultiselectNestedDropdown from "@app/shared/checkbox-dropdown/MultiselectNestedDropdown";
import { FC, JSX, useContext, useMemo } from "react";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ExportGapFilterType } from "@app/exports/enumerations";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { UserContext } from "@app/user/UserContext";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getServiceAreaGapTypes } from "@app/report-customization/build-reports/helpers/helpers";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import TooltipWrapper from "@app/shared/TooltipWrapper";

export const ServiceAreaSection: FC = () => {
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const isChangedGapsReport = !!rcContext.state.changedGapsType;
  const isNetNewProvider = rcContext.state.providerSource === ProviderNetworkTypeEnum.NetNew;
  const disableCurrentServiceAreaSection = isChangedGapsReport || isNetNewProvider;
  const allAreasString = "All Areas";

  const serviceAreaGapTypesOptions = useMemo(() => {
    const options = getServiceAreaGapTypes(user.roles, rcContext.state.reportType === ReportType.ZipAnalysisResultsReport);
    return options.map((o) => <option key={o.key} value={o.key}>{o.value}</option>);
  }, [user.roles]);

  const zipClassOptions = useMemo(() => {
    const zipClasses: JSX.Element[] = [];
    if (rcContext.state.reportType === ReportType.ZipClassAggregateResultsReport) {
      zipClasses.push(<option key="all" value="all">All Classifications</option>);
      const runConfig = rcContext.findTimeslice(rcContext.state.timeslice);
      if (!!runConfig) {
        zipClasses.push(...runConfig.zipClasses.map((zc) => <option key={zc} value={zc}>{zc}</option>));
      }
    }
    return zipClasses;
  }, [rcContext.state.reportType, rcContext.state.timeslice, rcContext.state.allTimeslices]);

  const allCountiesSelected = useMemo(() => {
    const allCounties = rcContext.state.allServiceAreaCounties.flatMap((state) => state.children.map((child) => child.key));
    return allCounties.length === rcContext.state.selectedCounties.length && allCounties.every((county) => rcContext.state.selectedCounties.includes(county));
  }, [rcContext.state.selectedCounties, rcContext.state.allServiceAreaCounties]);

  const allStatesSelected = useMemo(() => {
    if (rcContext.state.reportType !== ReportType.StateSummary) {
      return false;
    }

    const allStates = rcContext.state.allServiceAreaStates.map((s) => s.key);
    return allStates.length === rcContext.state.selectedStates.length && allStates.every((stateKey) => rcContext.state.selectedStates.includes(stateKey));
  }, [rcContext.state.reportType, rcContext.state.selectedStates, rcContext.state.allServiceAreaStates]);

  const selectedStates: DropdownItem<string>[] = useMemo(() => {
    if (rcContext.state.reportType !== ReportType.StateSummary) {
      return [];
    }

    return rcContext.state.allServiceAreaStates.map((asa) => {
      if (rcContext.state.selectedStates.includes(asa.key)) {
        return { key: asa.key, name: asa.name };
      }
      return null;
    }).filter((a) => !!a);
  }, [rcContext.state.reportType, rcContext.state.selectedStates, rcContext.state.allServiceAreaStates]);

  const allSpecialtiesServiceAreaString = useMemo(() => {
    const allCountiesWithStates = rcContext.state.allServiceAreaCounties.flatMap((s) => s.children.map((c) => ({ countyId: c.key, string: `${c.name}, ${s.name}`, })));
    return rcContext.state.selectedCounties.length === 1 ? allCountiesWithStates.find((c) => c.countyId === rcContext.state.selectedCounties[0])?.string
      : rcContext.state.selectedCounties.length > 1 ? allAreasString
        : "";
  }, [rcContext.state.allServiceAreaCounties, rcContext.state.selectedCounties]);

  const getNewNetworkAssessmentAnalysisTypes = (
    key: "includeActive" | "includePending" | "includeApproved",
    isChecked: boolean,
  ) => {
    const newNetworkAssessmentAnalysisTypes: NetworkAssessmentAnalysisType[] = [];
    const currentServiceAreaOptions = {
      includeActive: rcContext.state.includeActive,
      includeApproved: rcContext.state.includeApproved,
      includePending: rcContext.state.includePending,
      [key]: isChecked,
    };
    if (Object.values(currentServiceAreaOptions).some((v) => v)) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.ServiceArea);
    }
    if (rcContext.state.includeOpportunity) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.Opportunity);
    }
    return newNetworkAssessmentAnalysisTypes;
  }

  const changeIncludeActive = (isChecked: boolean) => {
    const newNetworkAssessmentAnalysisTypes = getNewNetworkAssessmentAnalysisTypes("includeActive", isChecked);
    rcContext.dispatch({
      payload: {
        includeActive: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: ReportCustomizationReducerActions.ChangeIncludeActive,
    })
  }

  const changeIncludePending = (isChecked: boolean) => {
    const newNetworkAssessmentAnalysisTypes = getNewNetworkAssessmentAnalysisTypes("includePending", isChecked);
    rcContext.dispatch({
      payload: {
        includePending: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: ReportCustomizationReducerActions.ChangeIncludePending,
    })
  }

  const changeIncludeApproved = (isChecked: boolean) => {
    const newNetworkAssessmentAnalysisTypes = getNewNetworkAssessmentAnalysisTypes("includeApproved", isChecked);
    rcContext.dispatch({
      payload: {
        includeApproved: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: ReportCustomizationReducerActions.ChangeIncludeApproved,
    })
  }

  const changeOpportunityOption = (isChecked: boolean) => {
    const newNetworkAssessmentAnalysisTypes = [];
    const currentServiceAreaOptions = {
      includeActive: rcContext.state.includeActive,
      includeApproved: rcContext.state.includeApproved,
      includePending: rcContext.state.includePending,
    };
    if (Object.values(currentServiceAreaOptions).some((v) => v)) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.ServiceArea);
    }
    if (isChecked) {
      newNetworkAssessmentAnalysisTypes.push(NetworkAssessmentAnalysisType.Opportunity);
    }

    rcContext.dispatch({
      payload: {
        includeOpportunity: isChecked,
        networkAssessmentAnalysisTypes: newNetworkAssessmentAnalysisTypes,
      },
      type: ReportCustomizationReducerActions.ChangeIncludeOpportunity,
    })
  }

  return (
    <>
      <div className="row">
        <h2 className="header-s col-auto">Service Area</h2>
      </div>
      <div className="row">
        {rcContext.state.reportType === ReportType.StateSummary ? (
          <MultiselectDropdown
            containerClassName="col-4"
            customSelectInputText={allStatesSelected ? allAreasString : undefined}
            items={rcContext.state.allServiceAreaStates}
            label="Service Area"
            onSelectionChanged={(items) => rcContext.dispatch({ payload: { selectedStates: items.map((k) => k.key) }, type: ReportCustomizationReducerActions.ChangeSelectedStates })}
            selectedItems={selectedStates}
            disabled={rcContext.state.allServiceAreaStates.length === 0 || disableCurrentServiceAreaSection}
            testId="report-customization__service-area--states-only"
          />
        ) : rcContext.state.reportType === ReportType.AdequacyAggregate ? (
          <MultiselectNestedDropdown
            containerClassName="col-4"
            customSelectInputText={allSpecialtiesServiceAreaString}
            items={rcContext.state.allServiceAreaCounties}
            label="Service Area"
            onSelectionChanged={(keys) => rcContext.dispatch({ payload: { selectedCounties: keys }, type: ReportCustomizationReducerActions.ChangeSelectedCounties })}
            selectedItems={rcContext.state.selectedCounties}
            disabled={rcContext.state.allServiceAreaCounties.length === 0 || disableCurrentServiceAreaSection}
            hideParentCheckboxes={true}
            hideSearch={true}
            maxSelectedChildren={1}
            testId="report-customization__service-area--single-select-states-and-counties"
            validationTooltipContent="You may only select one county or all counties for All Specialties reports."
          />
        ) : rcContext.state.reportType === ReportType.ZipClassAggregateResultsReport ? (
          <QESSelect
            containerClassName="col-4"
            label="Classification"
            onChange={(e) => rcContext.dispatch({ payload: { selectedZipClass: e.target.value }, type: ReportCustomizationReducerActions.ChangeSelectedZipClasses })}
            value={rcContext.state.selectedZipClass}
            disabled={zipClassOptions.length === 0}
            testId="report-customization__zip-classifications"
          >
            {zipClassOptions}
          </QESSelect>
        ) : (
          <MultiselectNestedDropdown
            containerClassName="col-4"
            customSelectInputText={allCountiesSelected ? allAreasString : undefined}
            items={rcContext.state.allServiceAreaCounties}
            label={(
              <>
                {rcContext.routeState.isZipProject && (
                  <TooltipWrapper
                    text={<div className="text-start">Reports will include zip codes based on the Primary Counties selected in the Service Area dropdown.<br />The Primary County designation is reflective of the zip code population centroid assignment.</div>}
                    position="right"

                  >
                    <QesIcon className="me-1" iconName={QesIcons.InfoCircle} description="focus to show tooltip" />
                  </TooltipWrapper>
                )} 
                Service Area
              </>
            )}
            onSelectionChanged={(keys) => rcContext.dispatch({ payload: { selectedCounties: keys }, type: ReportCustomizationReducerActions.ChangeSelectedCounties })}
            selectedItems={rcContext.state.selectedCounties}
            disabled={rcContext.state.allServiceAreaCounties.length === 0 || disableCurrentServiceAreaSection}
            testId="report-customization__service-area--states-and-counties"
          />
        )}

        {rcContext.state.reportType !== ReportType.ZipAnalysisAdequacyServicingProvider
          && rcContext.state.reportType !== ReportType.ZipClassAggregateResultsReport
          && rcContext.state.reportType !== ReportType.AdequacyAggregate
          && (rcContext.state.reportType !== ReportType.ZipAnalysisResultsReport || enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS))
          && (
            <QESSelect
              containerClassName="col-4"
              label="Service Area Gaps"
              value={rcContext.state.serviceAreaGapsType === ExportGapFilterType.NotMeetingAccess ? ExportGapFilterType.Gaps : rcContext.state.serviceAreaGapsType}
              onChange={(e) => rcContext.dispatch({ payload: { serviceAreaGapsType: e.target.value as ExportGapFilterType }, type: ReportCustomizationReducerActions.ChangeServiceAreaGapsType })}
              disabled={disableCurrentServiceAreaSection}
              testId="report-customization__export-gap-filter-type"
            >
              {serviceAreaGapTypesOptions}
            </QESSelect>
          )}

        {rcContext.state.reportType === ReportType.ZipAnalysisResultsReport
          && (
            <QESCheckbox
              containerClassName="col-auto align-self-end"
              label="Exclude 0 Membership"
              checked={rcContext.state.excludeZeroMembership}
              onChange={(e) => rcContext.dispatch({ payload: { excludeZeroMembership: e.target.checked }, type: ReportCustomizationReducerActions.ChangeExcludeZeroMembership })}
              disabled={!rcContext.state.timeslice}
              testId="report-customization__exclude-zero-membership"
            />
          )}
      </div>
      {rcContext.state.reportType !== ReportType.ZipAnalysisAdequacyServicingProvider
        && rcContext.state.reportType !== ReportType.ZipClassAggregateResultsReport
        && rcContext.state.reportType !== ReportType.ZipAnalysisResultsReport
        && (
          <>
            <QESCheckbox
              containerClassName="ms-1"
              label="Active counties"
              checked={rcContext.state.includeActive}
              onChange={(e) => changeIncludeActive(e.target.checked)}
              disabled={disableCurrentServiceAreaSection}
              testId="report-customization__include-active"
            />
            <QESCheckbox
              containerClassName="ms-1"
              label="Pending counties"
              checked={rcContext.state.includePending}
              onChange={(e) => changeIncludePending(e.target.checked)}
              disabled={disableCurrentServiceAreaSection}
              testId="report-customization__include-pending"
            />
            <QESCheckbox
              containerClassName="ms-1"
              label="Approved counties"
              checked={rcContext.state.includeApproved}
              onChange={(e) => changeIncludeApproved(e.target.checked)}
              disabled={disableCurrentServiceAreaSection}
              testId="report-customization__include-approved"
            />
            <QESCheckbox
              containerClassName="ms-1"
              label="Opportunity counties"
              checked={rcContext.state.includeOpportunity}
              onChange={(e) => changeOpportunityOption(e.target.checked)}
              disabled={disableCurrentServiceAreaSection}
              testId="report-customization__include-opportunity"
            />
          </>
        )}
    </>
  )
}